
import React, { useEffect, useRef, useState } from 'react'
import ApexCharts, { ApexOptions } from 'apexcharts'
import { getCSSVariableValue } from '../../../../../../_metronic/assets/ts/_utils'
import reportService from '@/projects/platform/services/report-service';
import { t } from 'i18next';
import { LoadingIndicator } from '@/core/utils/loading-indicator';


interface MonthlySalesQuantity {
  year: number
  month: number
  salesQuantity: number
}

type Props = {
  className: string
  chartColor: string
  chartHeight: string
}

const SalesQuantityChart: React.FC<Props> = ({ className, chartColor, chartHeight }) => {
  const [loadPanelVisible, setLoadPanelVisible] = useState(false);
  const [data, setData] = useState<MonthlySalesQuantity[]>([]);
  const chartRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    setLoadPanelVisible(true);
    reportService.getSupplierMontlySalesQuantity()
      .then((response) => {
        setData(response.data.searchResult.reverse());
        setLoadPanelVisible(false);
      })
      .catch((e) => {
        console.log(e);
        setLoadPanelVisible(false);
      });
  }, [])

  useEffect(() => {
    if (data.length > 0) {
      refreshChart();
    }
  }, [data, chartHeight, chartColor])

  const refreshChart = () => {

    if (!chartRef.current && !data) {
      return
    }
    const chart = new ApexCharts(chartRef.current, chartOptions(chartColor, chartHeight, data))
    if (chart) {
      chart.render()
    }

    return chart
  }

  return (
    <div className={`card ${className}`}>

      <div className='card-body p-0 d-flex justify-content-between flex-column overflow-hidden'>

        <div className='d-flex flex-stack flex-wrap flex-grow-1 px-9 pt-9 pb-3'>
          <div className='me-2'>
            <span className='fw-bold text-gray-800 d-block fs-3'>{t('sales')}</span>
            <span className='text-gray-500 fw-semibold'>{t('last_six_month')}</span>
          </div>
          {!loadPanelVisible && (<div className={`fw-bold fs-3 text-${chartColor}`}>{data.length > 0 ? `${data.reduce((sum: number, item: MonthlySalesQuantity) => sum + item.salesQuantity, 0)} ${t('adet')}` : '-'}</div>)}
        </div>

        {!loadPanelVisible ? (<div ref={chartRef} className='mixed-widget-10-chart'></div>) : (<LoadingIndicator panelClass='mb-2' spinnerClass='text-primary mb-20 mt-20' />)} 

      </div>
    </div>
  )
}

const chartOptions = (chartColor: string, chartHeight: string, data: MonthlySalesQuantity[]): ApexOptions => {
  const labelColor = getCSSVariableValue('--bs-gray-500')
  const borderColor = getCSSVariableValue('--bs-gray-200')
  const secondaryColor = getCSSVariableValue('--bs-gray-300')
  const baseColor = getCSSVariableValue('--bs-' + chartColor)


  return {
    series: [
      {
        name: t('sales_quantity'),
        data: data.map((x: MonthlySalesQuantity) => x.salesQuantity),
      },
    ],
    chart: {
      fontFamily: 'inherit',
      type: 'bar',
      height: chartHeight,
      toolbar: {
        show: false,
      },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '50%',
        borderRadius: 5,
      },
    },
    legend: {
      show: false,
    },
    dataLabels: {
      enabled: false,
    },
    stroke: {
      show: true,
      width: 2,
      colors: ['transparent'],
    },
    xaxis: {
      categories: data.map((x: MonthlySalesQuantity) => t('month' + x.month + '_short')),
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },

      labels: {
        style: {
          colors: labelColor,
          fontSize: '12px',
        },
      },
    },
    yaxis: {
      labels: {
        style: {
          colors: labelColor,
          fontSize: '12px',
        },
        formatter: function (value): string {
          return `${value} ${t('adet')}`;
        }
      },

    },
    fill: {
      type: 'solid',
    },
    states: {
      normal: {
        filter: {
          type: 'none',
          value: 0,
        },
      },
      hover: {
        filter: {
          type: 'none',
          value: 0,
        },
      },
      active: {
        allowMultipleDataPointsSelection: false,
        filter: {
          type: 'none',
          value: 0,
        },
      },
    },
    tooltip: {
      style: {
        fontSize: '12px',
      },
      y: {
        formatter: function (val) {
          return `${val} ${t('adet')}`;
        },
      },
    },
    colors: [baseColor, secondaryColor],
    grid: {
      padding: {
        top: 10,
      },
      borderColor: borderColor,
      strokeDashArray: 4,
      yaxis: {
        lines: {
          show: true,
        },
      },
    },
  }
}

export default SalesQuantityChart;